import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  IPlusDataV2Query,
  PosDataServiceMode,
  usePlusDataV2LazyQuery,
} from 'generated/graphql-gateway';
import { withLazySimpleCache } from 'state/graphql/with-simple-cache';
import { useServiceModeContext } from 'state/service-mode';

export type PosDataResults = {
  posData: Record<string, number> | null;
};

export type FetchPosDataFn = (args?: {
  storeNumber: string | null;
  isDelivery?: boolean;
}) => Promise<PosDataResults | null>;

export interface IUsePosData {
  data: PosDataResults;
  isLoading: boolean;
  refetch: FetchPosDataFn;
}

const useCachedPlusDataV2LazyQuery = withLazySimpleCache(usePlusDataV2LazyQuery);
// create PLU object to keep same logic
const transformToPluObject = (plus: IPlusDataV2Query['plusV2']): PosDataResults => {
  const initialPlus: Record<string, number> = {};
  return {
    posData: (plus || []).reduce((acc, plu) => {
      // We aliased the properties to reduce size.
      // We were hitting a hard limit on MenuService lambdas.
      acc[plu.k] = plu.v;
      return acc;
    }, initialPlus),
  };
};

type CachePOSData = {
  plusV2?: IPlusDataV2Query['plusV2'];
  data?: { plusV2: IPlusDataV2Query['plusV2'] };
};

export const usePosDataQuery = ({
  storeNumber,
  lazy = false,
}: {
  storeNumber: string | null;
  lazy?: boolean;
}): IUsePosData => {
  const { isDelivery } = useServiceModeContext();
  const [fetchPlusv2] = useCachedPlusDataV2LazyQuery();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<PosDataResults>({
    posData: null,
  });

  const fetchPosData: FetchPosDataFn = useCallback(
    async args => {
      let transformedResponse: PosDataResults = { posData: null };

      const storeId = args?.storeNumber ?? storeNumber;
      if (!storeId) {
        return transformedResponse;
      }

      setIsLoading(true);
      const res = ((await fetchPlusv2({
        allowFromCache: true,
        variables: {
          storeId,
          serviceMode: isDelivery ? PosDataServiceMode.DELIVERY : PosDataServiceMode.PICKUP,
        },
      })) as unknown) as CachePOSData;
      transformedResponse = transformToPluObject(res.data?.plusV2 || res?.plusV2);
      setData(transformedResponse);

      setIsLoading(false);

      return transformedResponse;
    },
    [isDelivery, storeNumber]
  );
  useEffect(() => {
    if (!lazy) {
      fetchPosData({
        storeNumber,
      });
    }
  }, [lazy, storeNumber, isDelivery, fetchPosData]);

  return useMemo(
    () => ({
      data,
      isLoading,
      refetch: fetchPosData,
    }),
    [data, isLoading, fetchPosData]
  );
};
